import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { Validators, FormArray, FormGroup, FormControl } from '@angular/forms';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { AngularFireStorage } from '@angular/fire/storage';
import { ModalService } from '@services/support/modal.service';
import { LoaderService } from '@services/support/loader.service';
import { UploadTaskSnapshot } from '@angular/fire/storage/interfaces';
import { FeatureUpdatesService } from '@services/feature-updates.service';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';

@Component({
  selector: 'app-feature-updates',
  templateUrl: './feature-updates.component.html',
  styleUrls: ['./feature-updates.component.scss']
})
export class FeatureUpdatesComponent implements OnInit, OnDestroy {

  @ViewChild("whatsNewTemplate", { static: true }) private whatsNewTemplate: TemplateRef<any>;
  @ViewChild("deleteVersionTemplate", { static: true }) private deleteVersionTemplate: TemplateRef<any>;

  featuresForm = null;
  isNewUpdate = false;
  newLangName: string;

  environmentNames = ['abus', 'alapala', 'coldjet', 'dev', 'dr', 'kubota', 'prime4', 'prod', 'realift', 'schunkgroup', 'solvace', 'technotrans', 'unival'];
  languageNames = ['en', 'de', 'fr', 'pt', 'ru', 'es', 'ja', 'tr', 'zhs', 'zht'];

  addableLanguages: { [l: string]: boolean } = { en: false, de: true, fr: true, pt: true, ru: true, es: true, ja: true, tr: true, zhs: true, zht: true };
  addableLanguagesArray = Object.keys(this.addableLanguages).filter(l => this.addableLanguages[l]);

  versions: any[] = null;
  versionsSub: Subscription = null;

  currentLastVersion: string = null;

  constructor(
    private afdb: AngularFireDatabase,
    private afst: AngularFireStorage,
    private featureUpdatesService: FeatureUpdatesService,
    private modalService: ModalService,
    private loaderService: LoaderService,
    private flashMessages: FlashMessagesService
  ) { }

  ngOnInit() {
    this.versionsSub = this.afdb.object<string>(`updates/web/currentLastVersion`).valueChanges().subscribe(currentVersion => {
      this.currentLastVersion = currentVersion;
    });

    this.versionsSub = this.afdb.list<any>(`updates/web/versions`).snapshotChanges().subscribe(versionsSnap => {
      this.versions = versionsSnap.map(snap => {
        const v = snap.payload.val();
        v.id = snap.key;
        return v;
      });
    });
  }

  onSaveCurrentLastVersion() {
    this.loaderService.show();
    (this.currentLastVersion === "" ? this.afdb.object<string>(`updates/web/currentLastVersion`).remove() : this.afdb.object<string>(`updates/web/currentLastVersion`).set(this.currentLastVersion))
      .then(() => this.flashMessages.show('Current Last Version updated.', { cssClass: 'alert-success', timeout: 6000, closeOnClick: true }))
      .catch(err => {
        console.log(err);
        this.flashMessages.show('An error occurred.', { cssClass: 'alert-danger', timeout: 6000, closeOnClick: true })
      })
      .finally(() => this.loaderService.hide());
  }

  ngOnDestroy() {
    if (this.versionsSub) { this.versionsSub.unsubscribe() }
  }

  onVersionClicked(version: any) {
    this.isNewUpdate = false;
    this.newLangName = null;

    const addableLanguages: { [l: string]: boolean } = {};
    const eList = [];
    for (const eName of this.environmentNames) {
      const lList = [];
      if (version[eName]) {
        for (const lName of Object.keys(version[eName])) {
          addableLanguages[lName] = false;
          const uList = [];
          for (const upd of Object.keys(version[eName][lName])) {
            uList.push(new FormGroup({
              id: new FormControl(upd),
              desc: new FormControl(version[eName][lName][upd].desc, [Validators.required]),
              image: new FormControl(version[eName][lName][upd].image, [Validators.required]),
              title: new FormControl(version[eName][lName][upd].title, [Validators.required]),
              file: new FormControl(null)
            }));
          }
          lList.push(new FormGroup({ updates: new FormArray(uList), langName: new FormControl({value: lName, disabled: true}, [Validators.required]) }));
        }
      }
      const env = new FormGroup({ languages: new FormArray(lList), envName: new FormControl({value: eName, disabled: true}, [Validators.required]) });  
      eList.push(env);
    }

    this.languageNames.filter(l => addableLanguages[l] !== false).forEach(l => {
      addableLanguages[l] = true;
    });
    this.addableLanguages = addableLanguages;
    this.addableLanguagesArray = Object.keys(this.addableLanguages).filter(l => this.addableLanguages[l]);

    this.featuresForm = new FormGroup({
      environments: new FormArray(eList),
      version: new FormControl(version.v, [Validators.required]),
      id: new FormControl(version.id)
    });
  }

  onNewVersion() {
    this.isNewUpdate = true;
    this.newLangName = null;

    const eList = [];
    for (const eName of this.environmentNames) {
      const lList = [];
      for (const lName of ['en']) {
        const uList = [new FormGroup({
          id: new FormControl(this.afdb.createPushId()),
          desc: new FormControl("", [Validators.required]),
          image: new FormControl("", [Validators.required]),
          title: new FormControl("", [Validators.required]),
          file: new FormControl(null)
        })];
        lList.push(new FormGroup({ updates: new FormArray(uList), langName: new FormControl({value: lName, disabled: true}, [Validators.required]) }));
      }
      const env = new FormGroup({ languages: new FormArray(lList), envName: new FormControl({value: eName, disabled: true}, [Validators.required]) });  
      eList.push(env);
    }

    this.featuresForm = new FormGroup({
      environments: new FormArray(eList),
      version: new FormControl("", [Validators.required]),
      id: new FormControl(this.afdb.createPushId())
    });
  }

  onDeleteVersion(event, version: any) {
    event.stopPropagation();

    const modalId = this.modalService.show({
      template: this.deleteVersionTemplate,
      context: {
        dataModel: null,
        callbacks: {
          cancel: () => this.modalService.hide(modalId),
          delete: () => {
            this.loaderService.show();
            this.deleteVersion(version)
            .then(() => this.modalService.hide(modalId))
            .catch(error => this.flashMessages.show('An error occurred.', { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true }))
            .finally(() => this.loaderService.hide());
          }
        }
      }
    });
  }

  deleteVersion(version: any) {
    return this.featureUpdatesService.deleteFeatureUpdatesFiles(version.id)
      .then(() => this.afdb.object(`updates/web/versions/${version.id}`).remove());
  }

  onDrop(event: CdkDragDrop<any[]>) {
    const environments = this.featuresForm.controls.environments as FormArray;
    for (const env of environments.controls) {
      const languages = (env as FormGroup).controls.languages as FormArray;
      for (const lang of languages.controls) {
        const updates = (lang as FormGroup).controls.updates as FormArray;
        moveItemInArray(updates.controls, event.previousIndex, event.currentIndex);
      }
    }
  }

  onIconChange(event, imageControl: FormControl, fileControl: FormControl) {
    if (event.target.files[0]) {
      imageControl.setValue(event.target.files[0].name);
      fileControl.setValue(event.target.files[0]);
    } else {
      imageControl.setValue("");
      fileControl.setValue(null);
    }
  }

  featuresFormSubmit() {
    const featureUpdate: any = {};
    const environments = this.featuresForm.controls.environments as FormArray;
    for (const env of environments.controls) {
      const languages = (env as FormGroup).controls.languages as FormArray;
      const envName = (env as FormGroup).controls.envName.value;
      featureUpdate[envName] = {};

      for (const lang of languages.controls) {
        const updates = (lang as FormGroup).controls.updates as FormArray;
        const langName = (lang as FormGroup).controls.langName.value;
        featureUpdate[envName][langName] = {};

        for (const upd of updates.controls) {
          const id = (upd as FormGroup).controls.id.value;
          featureUpdate[envName][langName][id] = {
            desc: (upd as FormGroup).controls.desc.value,
            image: (upd as FormGroup).controls.image.value,
            title: (upd as FormGroup).controls.title.value,
            file: (upd as FormGroup).controls.file.value
          }
        }
      }
    }
    const versionId = this.featuresForm.controls.id.value;
    featureUpdate.v = this.featuresForm.controls.version.value;

    this.loaderService.show();
    this.uploadVersionFiles(featureUpdate, versionId)
      .then(() => this.afdb.object(`updates/web/versions/${versionId}`).set(featureUpdate))
      .finally(() => {
        this.loaderService.hide();
        this.featuresForm = null;
      });
  }

  async uploadVersionFiles(version, versionId: string) {
    const uploadTasks: Promise<UploadTaskSnapshot>[] = [];
    const updateIds: string[] = [];
    for (const eName of this.environmentNames) {
      if (version[eName]) {
        for (const lName of Object.keys(version[eName])) {
          for (const upd of Object.keys(version[eName][lName])) {
            if (version[eName][lName][upd].file) {
              updateIds.push(upd);
              uploadTasks.push(this.afst.ref(`updates/web/versions/${versionId}/${upd}`).put(version[eName][lName][upd].file).then(t => t));
            }
          }
        }
      }
    }
    await Promise.all(uploadTasks);
    const updatePathsWithUrls = await this.featureUpdatesService.getFeatureUpdatesFileUrls(versionId, updateIds);

    for (const eName of this.environmentNames) {
      if (version[eName]) {
        for (const lName of Object.keys(version[eName])) {
          for (const upd of Object.keys(version[eName][lName])) {
            if (version[eName][lName][upd].file) {
              version[eName][lName][upd].image = updatePathsWithUrls[upd];
              delete version[eName][lName][upd].download_url;
            }
            // delete file anyway
            delete version[eName][lName][upd].file;
          }
        }
      }
    }
  }

  createLang(name: string, updateCount: number) {
    const uList = [];
    for (let i = 0; i < updateCount; i++) {
      uList.push(new FormGroup({
        id: new FormControl(this.afdb.createPushId()),
        desc: new FormControl("", [Validators.required]),
        image: new FormControl("", [Validators.required]),
        title: new FormControl("", [Validators.required]),
        file: new FormControl(null)
      }));
    }
    return new FormGroup({ updates: new FormArray(uList), langName: new FormControl({value: name, disabled: true}, [Validators.required]) });
  }

  addEnvironmentToFeatures(name: string) {
    const environments = this.featuresForm.controls.environments as FormArray;
    //const langs = ['en', 'de', 'fr', 'pt', 'ru', 'es', 'ja', 'tr', 'zhs', 'zht'].map(l => this.createLang(l));
    const langs = ['en'].map(l => this.createLang(l, 1));
    const env = new FormGroup({ languages: new FormArray(langs), envName: new FormControl({value: name, disabled: true},[Validators.required]) });
    environments.push(env);
  }

  addLanguageToEnvironments() {
    const environments = this.featuresForm.controls.environments as FormArray;
    //const env = environments.controls[envIndex] as FormGroup;

    let updateCount = 0;
    if (environments.length > 0) {
      const languages = ((environments.controls[0] as FormGroup).controls.languages as FormArray);
      if (languages.length > 0) {
        updateCount = ((languages.controls[0] as FormGroup).controls.updates as FormArray).length;
      }
    }

    for (const env of environments.controls) {
      const languages = (env as FormGroup).controls.languages as FormArray;
      languages.push(this.createLang(this.newLangName, updateCount));
    }
    this.addableLanguages[this.newLangName] = false;
    this.addableLanguagesArray = Object.keys(this.addableLanguages).filter(l => this.addableLanguages[l])
    this.newLangName = null;
  }

  addUpdateToLanguages(/*envIndex: number, langIndex: number*/) {
    const environments = this.featuresForm.controls.environments as FormArray;
    //const env = environments.controls[envIndex] as FormGroup;
    //const languages = env.controls.languages as FormArray;
    //const lang = languages.controls[langIndex] as FormGroup;
    //const updates = lang.controls.updates as FormArray;

    for (const env of environments.controls) {
      const languages = (env as FormGroup).controls.languages as FormArray;
      for (const lang of languages.controls) {
        const updates = (lang as FormGroup).controls.updates as FormArray;
        const updateCard = new FormGroup({
          id: new FormControl(this.afdb.createPushId()),
          desc: new FormControl("", [Validators.required]),
          image: new FormControl("", [Validators.required]),
          title: new FormControl("", [Validators.required]),
          file: new FormControl(null)
        });
        updates.push(updateCard);
      }
    }
  }

  removeLanguage(langName: string, langIndex: number) {
    const environments = this.featuresForm.controls.environments as FormArray;
    for (const env of environments.controls) {
      const languages = (env as FormGroup).controls.languages as FormArray;
      languages.removeAt(langIndex);
    }
    this.addableLanguages[langName] = true;
    this.addableLanguagesArray = Object.keys(this.addableLanguages).filter(l => this.addableLanguages[l]);
  }

  copyEnvironmentData(environments: FormArray, envIndex: number) {
    const copyEnvironment = (environments.controls[envIndex] as FormGroup);
    const copyLanguages = copyEnvironment.controls.languages as FormArray;

    for (const [i, env] of environments.controls.entries()) {
      if (i !== envIndex) {
        const languages = ((env as FormGroup).controls.languages as FormArray);
        languages.clear();

        for (const lang of copyLanguages.controls) {
          const langName = (lang as FormGroup).controls.langName.value;
          const updatesArray = ((lang as FormGroup).controls.updates as FormArray);

          const uList = [];
          for (const upd of updatesArray.controls) {
            uList.push(new FormGroup({
              id: new FormControl(this.afdb.createPushId()),
              desc: new FormControl((upd as FormGroup).controls.desc.value, [Validators.required]),
              image: new FormControl((upd as FormGroup).controls.image.value, [Validators.required]),
              title: new FormControl((upd as FormGroup).controls.title.value, [Validators.required]),
              file: new FormControl((upd as FormGroup).controls.file.value)
            }));
          }
          languages.push(new FormGroup({ updates: new FormArray(uList), langName: new FormControl({value: langName, disabled: true}, [Validators.required]) }));
        }
      }
    }
  }

  copyLanguageData(environmentGroup: FormGroup, langIndex: number) {
    const languages = environmentGroup.controls.languages as FormArray;
    const copyUpdates = ((languages.controls[langIndex] as FormGroup).controls.updates as FormArray);

    for (const [i, lang] of languages.controls.entries()) {
      if (i !== langIndex) {
        const updatesArray = ((lang as FormGroup).controls.updates as FormArray);
        updatesArray.clear();
        for (const upd of copyUpdates.controls) {
          const updateCard = new FormGroup({
            id: new FormControl(this.afdb.createPushId()),
            desc: new FormControl((upd as FormGroup).controls.desc.value, [Validators.required]),
            image: new FormControl((upd as FormGroup).controls.image.value, [Validators.required]),
            title: new FormControl((upd as FormGroup).controls.title.value, [Validators.required]),
            file: new FormControl((upd as FormGroup).controls.file.value)
          });
          updatesArray.push(updateCard);
        }
      }
    }
  }

  removeUpdate(updateIndex: number) {
    const environments = this.featuresForm.controls.environments as FormArray;
    for (const env of environments.controls) {
      const languages = (env as FormGroup).controls.languages as FormArray;
      for (const lang of languages.controls) {
        const updates = (lang as FormGroup).controls.updates as FormArray;
        updates.removeAt(updateIndex);
      }
    }
  }

  readURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => resolve(reader.result);
      reader.onerror = e => reject(e);
      reader.readAsDataURL(file);
    });
  };

  openUpdatesModal(updatesArray: FormArray) {
    const updates = updatesArray.controls.map(group => {
      const updateGroup = group as FormGroup;

      const result: any = {
        desc: updateGroup.controls.desc.value,
        title: updateGroup.controls.title.value
      }
      if (updateGroup.controls.file.value) {
        this.readURL(updateGroup.controls.file.value)
        .then(base64 => {
          result.image = base64;
        });
      } else {
        result.image = updateGroup.controls.image.value;
      }
      return result;
    });

    this.openWhatsNewModal(updates);
  }

  openWhatsNewModal(updates: any[]) {
    const modalId = this.modalService.show({
      template: this.whatsNewTemplate,
      context: {
        dataModel: { updates: updates },
        callbacks: {
          close: () => this.modalService.hide(modalId)
        }
      }
    });
  }
}
